import * as React from "react";
import type { MealKey } from "@/types/tracking";

export interface IconProps {
  size?: number;
  className?: string;
}

const baseProps = {
  fill: "none" as const,
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export const CafeIcon: React.FC<IconProps> = React.memo(({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" className={className} {...baseProps}>
    <path d="M22 6 Q 18 11, 22 16 Q 26 21, 22 26" strokeWidth={1.4} opacity={0.65} />
    <path d="M32 4 Q 28 9, 32 14 Q 36 19, 32 24" strokeWidth={1.4} opacity={0.85} />
    <path d="M42 6 Q 38 11, 42 16 Q 46 21, 42 26" strokeWidth={1.4} opacity={0.65} />
    <path d="M14 30 L44 30 L42 50 Q 41 56, 35 56 L23 56 Q 17 56, 16 50 Z" />
    <path d="M44 35 Q 53 35, 53 42 Q 53 49, 44 49" />
    <line x1="10" y1="59" x2="50" y2="59" strokeWidth={1.4} />
  </svg>
));
CafeIcon.displayName = "CafeIcon";

export const AlmocoIcon: React.FC<IconProps> = React.memo(({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" className={className} {...baseProps}>
    <circle cx="32" cy="36" r="13" />
    <circle cx="32" cy="36" r="9" strokeWidth={1.1} opacity={0.4} />
    <line x1="11" y1="10" x2="11" y2="20" strokeWidth={1.4} />
    <line x1="14" y1="10" x2="14" y2="20" strokeWidth={1.4} />
    <line x1="17" y1="10" x2="17" y2="20" strokeWidth={1.4} />
    <line x1="14" y1="20" x2="14" y2="56" strokeWidth={2.2} />
    <path d="M50 10 Q 53 13, 53 18 L53 26 L47 26 L47 14 Q 47 11, 50 10 Z" strokeWidth={1.4} />
    <line x1="50" y1="26" x2="50" y2="56" strokeWidth={2.2} />
  </svg>
));
AlmocoIcon.displayName = "AlmocoIcon";

export const LancheIcon: React.FC<IconProps> = React.memo(({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" className={className} {...baseProps}>
    <path d="M32 18 C 22 18, 14 24, 14 36 C 14 48, 22 58, 32 58 C 42 58, 50 48, 50 36 C 50 24, 42 18, 32 18 Z" />
    <path d="M27 18 Q 32 14, 37 18" strokeWidth={1.4} />
    <path d="M32 14 L34 7" strokeWidth={1.8} />
    <path d="M34 7 Q 42 5, 44 12 Q 38 14, 34 11 Z" strokeWidth={1.4} />
  </svg>
));
LancheIcon.displayName = "LancheIcon";

export const JantarIcon: React.FC<IconProps> = React.memo(({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" className={className} {...baseProps}>
    <path d="M22 10 Q 18 16, 22 22" strokeWidth={1.4} opacity={0.6} />
    <path d="M32 8 Q 28 14, 32 20" strokeWidth={1.4} opacity={0.85} />
    <path d="M42 10 Q 38 16, 42 22" strokeWidth={1.4} opacity={0.6} />
    <path d="M8 28 L56 28" />
    <path d="M10 28 Q 14 52, 32 54 Q 50 52, 54 28" />
    <ellipse cx="32" cy="29" rx="22" ry="3" strokeWidth={1.4} opacity={0.4} />
    <line x1="46" y1="14" x2="42" y2="26" strokeWidth={1.4} />
    <ellipse cx="47" cy="12" rx="3" ry="2" transform="rotate(-25 47 12)" strokeWidth={1.4} />
  </svg>
));
JantarIcon.displayName = "JantarIcon";

export const Coringa1Icon: React.FC<IconProps> = React.memo(({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" className={className} {...baseProps}>
    <path d="M16 28 Q 12 18, 16 8 Q 22 12, 22 22 L20 28" />
    <path d="M28 26 Q 28 14, 36 8 Q 40 18, 34 26" />
    <path d="M40 28 Q 44 18, 50 16 Q 50 24, 44 28" />
    <circle cx="30" cy="28" r="3.5" />
    <line x1="30" y1="24.5" x2="30" y2="22" strokeWidth={1.4} />
    <line x1="6" y1="34" x2="58" y2="34" strokeWidth={1.4} />
    <path d="M8 34 Q 10 56, 32 58 Q 54 56, 56 34" />
  </svg>
));
Coringa1Icon.displayName = "Coringa1Icon";

// Coringa 2 = Sub/baguete horizontal (decisão 2026-05-18 via skill frontend-design)
export const Coringa2Icon: React.FC<IconProps> = React.memo(({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" className={className} {...baseProps}>
    <path d="M7 36 Q 7 30, 13 30 L51 30 Q 57 30, 57 36 L57 44 Q 57 50, 51 50 L13 50 Q 7 50, 7 44 Z" />
    <path d="M11 30 Q 15 24, 19 30 Q 23 24, 27 30 Q 31 24, 35 30 Q 39 24, 43 30 Q 47 24, 51 30" strokeWidth={1.4} />
    <line x1="18" y1="36" x2="20" y2="46" strokeWidth={1.4} opacity={0.4} />
    <line x1="30" y1="36" x2="32" y2="46" strokeWidth={1.4} opacity={0.4} />
    <line x1="42" y1="36" x2="44" y2="46" strokeWidth={1.4} opacity={0.4} />
  </svg>
));
Coringa2Icon.displayName = "Coringa2Icon";

// Status icons (24×24 viewBox, color via parent)
export const StatusDoneIcon: React.FC<IconProps> = React.memo(({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className}
       fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6.5 L9.5 17.5 L4 12.5" />
  </svg>
));
StatusDoneIcon.displayName = "StatusDoneIcon";

export const StatusPartialIcon: React.FC<IconProps> = React.memo(({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className}
       fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9.5" strokeDasharray="3 2" />
    <path d="M12 8 L12 13" strokeWidth={2.5} />
    <circle cx="12" cy="16.5" r="0.6" fill="currentColor" stroke="none" />
  </svg>
));
StatusPartialIcon.displayName = "StatusPartialIcon";

export const StatusSkipIcon: React.FC<IconProps> = React.memo(({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className}
       fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 17 L7 7" />
    <path d="M7 17 L17 7" />
  </svg>
));
StatusSkipIcon.displayName = "StatusSkipIcon";

// Water vessels
export const VesselCup200Icon: React.FC<IconProps> = React.memo(({ size = 32, className }) => (
  <svg width={size} height={(size * 86) / 56} viewBox="0 0 56 86" className={className} {...baseProps}>
    <path d="M12 30 L44 30 L42 80 Q 42 84, 38 84 L18 84 Q 14 84, 14 80 Z" />
    <path d="M14 50 L42 50" strokeWidth={1.3} opacity={0.45} />
    <path d="M20 60 Q 22 65, 20 70" strokeWidth={1.4} opacity={0.6} />
  </svg>
));
VesselCup200Icon.displayName = "VesselCup200Icon";

export const VesselBottle500Icon: React.FC<IconProps> = React.memo(({ size = 32, className }) => (
  <svg width={size} height={(size * 114) / 70} viewBox="0 0 70 114" className={className} {...baseProps}>
    <rect x="24" y="6" width="22" height="10" rx="2" />
    <line x1="28" y1="11" x2="42" y2="11" strokeWidth={1.4} opacity={0.5} />
    <path d="M24 16 L24 24 Q 24 26, 22 28 L18 30" />
    <path d="M46 16 L46 24 Q 46 26, 48 28 L52 30" />
    <path d="M18 30 L18 102 Q 18 108, 26 108 L44 108 Q 52 108, 52 102 L52 30" />
    <path d="M19 60 L51 60" strokeWidth={1.3} opacity={0.45} />
    <rect x="24" y="68" width="22" height="14" strokeWidth={1.4} opacity={0.3} />
    <path d="M26 88 Q 28 94, 26 100" strokeWidth={1.4} opacity={0.55} />
  </svg>
));
VesselBottle500Icon.displayName = "VesselBottle500Icon";

export const VesselBottle1LIcon: React.FC<IconProps> = React.memo(({ size = 32, className }) => (
  <svg width={size} height={(size * 138) / 84} viewBox="0 0 84 138" className={className} {...baseProps}>
    <rect x="28" y="6" width="28" height="12" rx="2" />
    <line x1="32" y1="12" x2="52" y2="12" strokeWidth={1.4} opacity={0.5} />
    <path d="M28 18 L28 28 Q 28 30, 26 32 L22 35" />
    <path d="M56 18 L56 28 Q 56 30, 58 32 L62 35" />
    <path d="M22 35 L22 124 Q 22 132, 32 132 L52 132 Q 62 132, 62 124 L62 35" />
    <path d="M23 75 L61 75" strokeWidth={1.3} opacity={0.45} />
    <rect x="28" y="86" width="28" height="20" strokeWidth={1.4} opacity={0.3} />
    <path d="M30 112 Q 33 118, 30 124" strokeWidth={1.4} opacity={0.6} />
  </svg>
));
VesselBottle1LIcon.displayName = "VesselBottle1LIcon";

// Map: meal_key -> Icon component (used by views to lookup the right icon)
export const MEAL_ICON_BY_KEY: Record<MealKey, React.FC<IconProps>> = {
  cafe: CafeIcon,
  almoco: AlmocoIcon,
  lanche: LancheIcon,
  jantar: JantarIcon,
  coringa_1: Coringa1Icon,
  coringa_2: Coringa2Icon,
  lanche_manha: LancheIcon,
  lanche_tarde: LancheIcon,
  ceia: JantarIcon,
};
